import { apiClient } from './http/client'

export type AccountIdentity = { source: string; user_id: string }
export type AccountProjection = AccountIdentity & {
  tenant_id: string; instance_id: string | null; effective_instance_id: string
  effective_instance_display_name?: string | null
  status: 'active' | 'disabled'; revision: number; exists: boolean
  updated_by_source?: string | null; updated_by_user_id?: string | null; updated_at?: string | null
}
export type AssignmentUpdate = AccountIdentity & {
  instance_id: string | null; status: 'active' | 'disabled'; expected_revision: number
}
export type AccountsPage = { accounts: AccountProjection[]; total: number; page: number; page_size: number }
export type InstanceDirectory = { default_instance_id: string; instances: {
  instance_id: string; display_name?: string | null; domain: string; domain_pack_version: string; enabled: boolean
  tenant_id?: string; specs_path?: string; avatar_key?: AvatarKey | null
}[]; catalog_revision?: number; role_creation_available?: boolean }
export type AvatarKey = 'user' | 'team' | 'shop' | 'solution' | 'robot'
export type RoleCreationCommand = { request_id: string; expected_catalog_revision: number; source_instance_id: string
  display_name: string; avatar_key: AvatarKey }
export type RoleCreationResult = { request_id: string; instance: InstanceDirectory['instances'][number]; catalog_revision: number }
export type AdministrationAccess = { user_id: string }

const record = (value: unknown): value is Record<string, unknown> => (
  !!value && typeof value === 'object' && !Array.isArray(value)
)
const text = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0
const optionalName = (value: unknown): boolean => value === undefined || value === null || text(value)
const integer = (value: unknown, minimum = 0): value is number => (
  typeof value === 'number' && Number.isInteger(value) && value >= minimum
)
const invalid = (): never => { throw new TypeError('管理响应不可用') }
const validInstance = (item: unknown) => record(item) && text(item.instance_id) && text(item.domain)
  && text(item.domain_pack_version) && typeof item.enabled === 'boolean' && optionalName(item.display_name)
  && (item.avatar_key === undefined || item.avatar_key === null || ['user', 'team', 'shop', 'solution', 'robot'].includes(String(item.avatar_key)))
const account = (value: unknown, identity?: AccountIdentity): AccountProjection => {
  if (!record(value) || !text(value.tenant_id) || typeof value.source !== 'string' || !text(value.user_id)
    || !(value.instance_id === null || text(value.instance_id)) || !text(value.effective_instance_id)
    || !['active', 'disabled'].includes(String(value.status)) || !integer(value.revision)
    || typeof value.exists !== 'boolean' || !optionalName(value.effective_instance_display_name)) return invalid()
  if (identity && (value.source !== identity.source || value.user_id !== identity.user_id)) return invalid()
  return value as AccountProjection
}

export const administrationApi = {
  access: async (signal?: AbortSignal): Promise<AdministrationAccess> => {
    const { data } = await apiClient.get<unknown>('/instance-admin/access', { signal })
    if (!record(data) || !text(data.user_id)) return invalid()
    return { user_id: data.user_id }
  },
  instances: async (signal?: AbortSignal): Promise<InstanceDirectory> => {
    const { data } = await apiClient.get<unknown>('/instance-admin/instances', { signal })
    if (!record(data) || !text(data.default_instance_id) || !Array.isArray(data.instances)
      || !data.instances.every(validInstance) || (data.catalog_revision !== undefined && !integer(data.catalog_revision))
      || (data.role_creation_available !== undefined && typeof data.role_creation_available !== 'boolean')) return invalid()
    return data as InstanceDirectory
  },
  create: async (command: RoleCreationCommand, signal?: AbortSignal): Promise<RoleCreationResult> => {
    const { data } = await apiClient.post<unknown>('/instance-admin/instances', command, { signal })
    if (!record(data) || data.request_id !== command.request_id || !validInstance(data.instance)
      || !integer(data.catalog_revision, 1)) return invalid()
    return data as RoleCreationResult
  },
  accounts: async (query: string, page: number, signal?: AbortSignal): Promise<AccountsPage> => {
    const { data } = await apiClient.get<unknown>('/instance-admin/accounts', {
      params: { query, page, page_size: 20 }, signal,
    })
    if (!record(data) || !Array.isArray(data.accounts) || !integer(data.total)
      || !integer(data.page, 1) || !integer(data.page_size, 1)) return invalid()
    return { accounts: data.accounts.map((item) => account(item)), total: data.total,
      page: data.page, page_size: data.page_size }
  },
  assignment: async (identity: AccountIdentity, signal?: AbortSignal): Promise<AccountProjection> => {
    const { data } = await apiClient.get<unknown>('/instance-admin/assignment', { params: identity, signal })
    return account(data, identity)
  },
  save: async (command: AssignmentUpdate, signal?: AbortSignal): Promise<AccountProjection> => {
    const { data } = await apiClient.put<unknown>('/instance-admin/assignment', command, { signal })
    return account(data, command)
  },
}
